import type { Incident } from '@analyzer/client';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useIncidentListQuery } from '../api/incident.js';
import { formatDateUTC } from '../components/utils.js';

type SortField = 'projectName' | 'testTarget' | 'snapshotId' | 'snapshotCreatedDate' | 'alertCount';
type SortOrder = 'asc' | 'desc';

function compare(a: Incident, b: Incident, field: SortField, order: SortOrder): number {
  const sign = order === 'asc' ? 1 : -1;
  const av = a[field];
  const bv = b[field];

  // Nulls sort last regardless of direction.
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;

  if (typeof av === 'number' && typeof bv === 'number') return sign * (av - bv);
  return sign * String(av).localeCompare(String(bv));
}

export function IncidentList() {
  const { data: incidents, isLoading, isError, error } = useIncidentListQuery();
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('snapshotCreatedDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const { enqueueSnackbar } = useSnackbar();

  const filtered = useMemo(() => {
    if (!incidents) return [];
    const needle = search.trim().toLowerCase();
    const list = needle
      ? incidents.filter((i) => (i.projectName ?? '').toLowerCase().includes(needle))
      : incidents.slice();
    list.sort((a, b) => compare(a, b, sortField, sortOrder));
    return list;
  }, [incidents, search, sortField, sortOrder]);

  if (isLoading) return <CircularProgress />;
  if (isError) return <Alert severity="error">{`Error: ${error.message}`}</Alert>;

  const onSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const onCopy = (url: string) => {
    void navigator.clipboard.writeText(url);
    enqueueSnackbar('URL copied', { variant: 'info' });
  };

  return (
    <Paper elevation={1} sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Incidents</Typography>
        <TextField
          size="small"
          label="Project"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by project name"
        />
      </Box>

      {filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {incidents && incidents.length === 0
            ? 'No archived incidents yet.'
            : 'No incidents match the filter.'}
        </Typography>
      ) : (
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortField === 'projectName' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortField === 'projectName'}
                    direction={sortField === 'projectName' ? sortOrder : 'asc'}
                    onClick={() => onSort('projectName')}
                  >
                    Project
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'testTarget' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortField === 'testTarget'}
                    direction={sortField === 'testTarget' ? sortOrder : 'asc'}
                    onClick={() => onSort('testTarget')}
                  >
                    Test target
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'snapshotId' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortField === 'snapshotId'}
                    direction={sortField === 'snapshotId' ? sortOrder : 'asc'}
                    onClick={() => onSort('snapshotId')}
                  >
                    Snapshot
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'snapshotCreatedDate' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortField === 'snapshotCreatedDate'}
                    direction={sortField === 'snapshotCreatedDate' ? sortOrder : 'asc'}
                    onClick={() => onSort('snapshotCreatedDate')}
                  >
                    Date (UTC)
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'alertCount' ? sortOrder : false}>
                  <TableSortLabel
                    active={sortField === 'alertCount'}
                    direction={sortField === 'alertCount' ? sortOrder : 'asc'}
                    onClick={() => onSort('alertCount')}
                  >
                    Alerts
                  </TableSortLabel>
                </TableCell>
                <TableCell>Bundle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row) => {
                const detailHref =
                  row.projectId !== null
                    ? `/projects/${row.projectId}/snapshots/web/${row.snapshotId}`
                    : null;
                return (
                  <TableRow
                    key={row.snapshotId}
                    hover={!!detailHref}
                    sx={detailHref ? { cursor: 'pointer' } : undefined}
                    onClick={(e) => {
                      if (!detailHref) return;
                      // Don't hijack clicks on the bundle action buttons.
                      if ((e.target as HTMLElement).closest('button, a')) return;
                      window.location.href = detailHref;
                    }}
                  >
                    <TableCell>{row.projectName ?? <em>(orphan)</em>}</TableCell>
                    <TableCell>{row.testTarget ?? '—'}</TableCell>
                    <TableCell>
                      {detailHref ? <Link to={detailHref}>{row.snapshotId}</Link> : row.snapshotId}
                    </TableCell>
                    <TableCell>
                      {row.snapshotCreatedDate ? formatDateUTC(new Date(row.snapshotCreatedDate)) : '—'}
                    </TableCell>
                    <TableCell>{row.alertCount ?? '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Open">
                          <IconButton
                            size="small"
                            component="a"
                            href={row.bundleUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <LaunchIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy URL">
                          <IconButton size="small" onClick={() => onCopy(row.bundleUrl)}>
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
